<template>
    <div class="staffs">
        <slot name="header">
            <h2 class="mt-4">
                {{ $l10n.__('Attribution des licences') }}
            </h2>
        </slot>
        <div class="flex-row br staff-form-container fs-3 p-2 mt-5 mb-1 gray-0-bkg">
            <el-input
                slot="reference"
                ref="search-input"
                v-model="search"
                suffix-icon="icon-search"
                onkeydown="return (event.keyCode!=13);"
                :placeholder="$t.__('pages.account.Staffs.search')"
            />
            <span class="semibold mr-3">{{
                $l10n.__('%s collaborateurs au total', staffTotal)
            }}</span>
        </div>

        <el-table :data="filteredStaffList" max-height="617" style="width: 100%">
            <el-table-column :label="$t.__('common.labels.name')">
                <template slot-scope="scope">
                    <div class="user-information">
                        <avatar-randomized
                            :firstname="scope.row.firstname"
                            :lastname="scope.row.lastname"
                            :avatar-image="scope.row.avatar"
                        />
                        <div class="ml-2 staff-name-container">
                            <div class="semibold staff-name">
                                {{ scope.row.firstname + ' ' + scope.row.lastname }}
                            </div>
                            <div class="fs-2 gray-60" v-if="scope.row.job">
                                {{ scope.row.job.name }}
                            </div>
                        </div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column>
                <template slot-scope="scope">
                    <chip-label
                        v-if="staffRole(scope.row)"
                        :color="staffRole(scope.row).backgroundColor"
                        :label="staffRole(scope.row).label"
                    />
                </template>
            </el-table-column>
            <el-table-column :width="224" :label="$t.__('common.labels.licence_type')">
                <template slot-scope="scope">
                    <el-tooltip placement="bottom-start" :disabled="!isReadOnly">
                        <div slot="content">
                            {{ $l10n.__('Pour modifier la licence de ce collaborateur') }}<br />
                            {{ $l10n.__('merci de contacter un commercial') }}
                        </div>
                        <el-select
                            class="w-2"
                            :disabled="isReadOnly"
                            :value="licenceDistributionForStaff(scope.row.id)"
                            @change="handleLicenceChange($event, scope.row)"
                        >
                            <el-option
                                :value="null"
                                :label="$t.__('common.labels.none')"
                            ></el-option>
                            <el-option
                                v-for="license in availablesProducts"
                                :key="license.code"
                                :label="license.title"
                                :value="license.code"
                            >
                            </el-option>
                        </el-select>
                    </el-tooltip>
                </template>
            </el-table-column>

            <infinite-loading
                slot="append"
                force-use-infinite-wrapper=".el-table__body-wrapper"
                @infinite="infiniteHandler"
            >
                <div slot="spinner" class="py-2">{{ $l10n.__('Chargement en cours...') }}</div>
                <div slot="no-more"></div>
                <div slot="no-results"></div>
            </infinite-loading>
        </el-table>
    </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import { createNamespacedHelpers } from 'vuex';
import AvatarRandomized from 'vcomponents/utilities/AvatarRandomized.vue';
import ChipLabel from 'vcomponents/utilities/ChipLabel.vue';

const renewCart = createNamespacedHelpers('renewCart');

export default {
    props: {
        isReadOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            search: '',
            loading: [],
        };
    },
    computed: {
        ...renewCart.mapGetters([
            'availablesProducts',
            'staffs',
            'licenceDistribution',
            'staffTotal',
            'staffLimit',
            'staffCount',
        ]),
        licenceDistributionForStaff() {
            return (staffId) => {
                const licence = this.licenceDistribution.find((item) => item.staff === staffId);
                return licence?.code || null;
            };
        },
        filteredStaffList() {
            let tempStaffList = this.staffs;

            if (this.search !== '') {
                tempStaffList = tempStaffList.filter(
                    (staff) =>
                        staff.firstname.toLowerCase().startsWith(this.search.toLowerCase()) ||
                        staff.lastname.toLowerCase().startsWith(this.search.toLowerCase()),
                );
            }

            return tempStaffList;
        },
        staffRole() {
            return (staff) => {
                if (staff.is_creator) {
                    return {
                        backgroundColor: '#FF6060',
                        label: 'Créateur + Admin',
                    };
                } else if (staff.profile === 'admin') {
                    return {
                        backgroundColor: '#FF6060',
                        label: 'Administrateur',
                    };
                } else if (staff.is_decision_maker) {
                    return {
                        backgroundColor: '#F6C243',
                        label: 'Décisionnaire',
                    };
                } else {
                    return null;
                }
            };
        },
    },
    methods: {
        ...renewCart.mapMutations(['setLicenceDistribution']),
        ...renewCart.mapActions(['fetchStaffs', 'updateLicencesQuantity']),
        handleLicenceChange(value, staff) {
            this.updateLicencesQuantity({ value, staff });
        },
        infiniteHandler($state) {
            // On first load block Infinite scroll for waiting Fetch Staffs on
            if (!this.staffTotal && !this.loading.length) {
                $state.reset();
                return;
            }

            // When total staffs is reached on first call, don't make it twice for nothing
            if (this.staffTotal === this.staffCount) {
                $state.complete();
                return;
            }

            this.fetchStaffs().then(({ data }) => {
                if (data.pagination.count === 0) {
                    $state.complete();
                } else {
                    $state.loaded();
                }
                this.loading.push(...Array.from(data.data, (staff) => false));
            });
        },
    },
    components: {
        AvatarRandomized,
        InfiniteLoading,
        ChipLabel,
    },
};
</script>

<style lang="scss" scoped>
.staffs {
    &::v-deep {
        .el-table {
            td {
                padding: 12px 0;
                .cell {
                    padding-left: 16px;
                }
            }
        }
    }
    .staff-name-container {
        min-width: 0;
    }
    .staff-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        word-break: normal;
    }
}

.staff-form-container {
    align-items: center;
    justify-content: space-between;
    &::v-deep {
        .el-input {
            width: 260px;
        }
    }
}

.user-information {
    align-items: center;
    display: flex;
    .avatar {
        flex-shrink: 0;
    }
}

.el-tooltip__popper {
    padding-bottom: 6px;
}
</style>
